import { UserOutlined } from '@ant-design/icons'
import { gql, useMutation, useQuery } from '@apollo/client'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select,
  SelectProps,
  Typography,
} from 'antd'
import { useForm } from 'antd/lib/form/Form'
import dayjs from 'dayjs'
import { debounce, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { queryCache, useDemandeUpdate } from '../../apollo/hooks'
import { DEVIS_FOURNISSEUR_MUTATION } from '../../apollo/mutation/devisfournisseur.mutation'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { FICHIER_QUERY } from '../../apollo/mutation/fichier.mutation'
import { EVENEMENTS } from '../../apollo/query/demande.query'
import { C_S_QUERY } from '../../apollo/query/personne.query'
import { AWS_S3_URL, AWS_S3_RESIZED_URL, COLOR, DATE_FORMAT } from '../../constants'
import { formatAddress, formatMembreCSOptions, validateEmail } from '../../helper'
import { useAuth, useSearchPersonneByCopro, useUpdateKanban } from '../../lib/hooks'
import {
  D4_Conseilsyndical,
  D4_Demandedevis,
  D4_Demandedevis_Set_Input,
  D4_Devisfournisseur,
  D4_Devisfournisseur_Set_Input,
  D4_Evenementdemande_Insert_Input,
  D4_Fichier,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Fournisseur_Favori_Equipement,
  D4_Interventiondemande_Insert_Input,
  D4_Lot,
  D4_Personne,
  D4_Societe,
  D4_Typetantieme,
} from '../../types'
import MSGraphAPI from '../Courriel/ms-graph.api'
import D4SelectSociete from '../D4SelectSociete'
import { DatePicker } from '../DatePicker'
import SocieteDescription from '../Description/SocieteDescription'
import Thumbnail from '../Thumbnail'

const TYPE_TANTIEME_QUERY = gql`
  query TypeTantieme {
    d4_typetantieme {
      id
      type
    }
  }
`

const DEMANDE_INTERVENTION_COUNT_QUERY = gql`
  query DemandeInterventoinCount($demandeId: bigint!) {
    d4_interventiondemande_aggregate(where: { demande_id: { _eq: $demandeId } }) {
      aggregate {
        count
      }
    }
  }
`

const DEMANDE_INTERVENTION_MUTATION = gql`
  mutation DemandeIntervention($data: d4_interventiondemande_insert_input!) {
    insert_d4_interventiondemande_one(object: $data) {
      id
      daterappel
      dateaccordcs
      datecreationdemande
      demande_id
      dtype
      reference
      objet
      lieuintervention
    }
  }
`

const CLOTURE_DEVIS_MUTATION = gql`
  mutation ClotureDevis($id: bigint!, $data: d4_demandedevis_set_input!) {
    update_d4_demandedevis_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
      datecloture
    }
  }
`

const LOTS_QUERY = gql`
  query Lots($trigramme: String!) {
    d4_lot(where: { copropriete: { trigramme: { _eq: $trigramme } } }) {
      tantiemes {
        typetantieme_id
      }
    }
  }
`

const Coproprietaires: React.FC<
  Omit<SelectProps<any>, 'onChange'> & { trigramme: string; onChange: (value: any, option: D4_Lot) => void }
> = ({ trigramme, ...selectProps }) => {
  const search = useSearchPersonneByCopro(trigramme)
  const [options, setOptions] = useState<Array<any>>([])
  const [lots, setLots] = useState<D4_Lot[]>()

  const handleSearch = debounce(async (text: string) => {
    const res = await search({ search: text })
    const lots = uniqBy(res?.data.lots, 'lot_coproprietaire.coproprietaire.personne.id')
    const personneOptions =
      lots.map((lot) => ({
        label: [
          <UserOutlined style={{ marginRight: '5px', color: COLOR.blue }} />,
          `${lot.copropriete?.trigramme} - ${lot.lot_coproprietaire?.coproprietaire?.personne?.nom} - ${lot.lot_coproprietaire?.coproprietaire?.personne?.prenom} - ${lot.nature}}`,
        ],
        value: lot.lot_coproprietaire?.coproprietaire?.personne?.id,
      })) ?? []

    setLots(lots)
    setOptions(personneOptions)
  }, 300)

  const handleChange = (value: any, option: any) => {
    const lot = lots?.find((lot) => lot.lot_coproprietaire?.coproprietaire?.personne?.id === value)
    selectProps.onChange?.(value, lot as D4_Lot)
  }

  return (
    <Select
      {...selectProps}
      onSearch={handleSearch}
      showSearch
      options={selectProps.options ? selectProps.options?.concat(options) : options}
      optionFilterProp="label"
      onChange={handleChange}
    />
  )
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
}

const tailLayout = {
  wrapperCol: { offset: 22 },
}

let isPdf = (texte: string) => /\.(pdf)$/i.test(texte ?? '')

interface ModelMailProps {
  copro: string
  objet: string
  mailsBinome: string
  reference: string
  gestionnaire: string
  contactsSurPlace: D4_Personne[] | undefined
  rappel: string
  checked: D4_Fn_View_Evenements[]
}

const modelMail = ({
  copro,
  objet,
  mailsBinome,
  reference,
  gestionnaire,
  contactsSurPlace,
  rappel,
  checked,
}: ModelMailProps) => `
Bonjour,</br></br>
Demande d’intervention sur : ${copro}</br></br>
Merci d’intervenir ${objet.replaceAll('\n', '</br>')}</br>
${
  rappel
    ? `- rappeler le nom du copropriétaire sur la facture : <strong>${rappel}</strong> (par défaut sur le libellé de la demande)</br></br>`
    : ''
}

Contacts sur place :</br>
${
  contactsSurPlace
    ?.map((contact) => `${contact.nom} ${contact.prenom} - ${contact.telportable} ${contact?.telfixe ?? ''}`)
    .join('</br>') ?? 'Aucun'
}</br></br>

<strong style="color: red;">IMPORTANT</strong> :</br>
- nous confirmer la date de votre intervention prévisionnelle.</br></br>
- nous confirmer que l’intervention est terminée.</br></br>
- rappeler notre référence : <strong style="text-decoration: underline;">${reference}</strong>, sur votre facture sinon nous ne pourrons pas la traiter.</br></br>
- toujours envoyer vos factures au service FACTURES à : factures@d4i.fr</br></br>

Restant à votre entière disposition vous pouvez nous solliciter par mail ${mailsBinome} ou au 04 91 26 10 75</br>
Sincères salutations,</br></br>
${checked
  ?.map(
    (ev) =>
      `<div>${ev.auteurnom} - ${ev.auteurprenom}<br />${
        ev.fichier
          ? `<a href="${AWS_S3_URL}${ev.texte}" target="_blank" >
        <img width="150px" src="${AWS_S3_RESIZED_URL}resized-${ev.texte}${
              isPdf(ev.texte ?? '') ? '.png' : ''
            }" />
      </a>`
          : ev.texte
      }</div><hr><br />`
  )
  
}</br></br>
${gestionnaire}
`

const DIForm = ({
  demande,
  onSubmit,
  fichierId,
}: {
  demande: D4_Fn_View_Demande
  onSubmit: any
  fichierId?: number
}) => {
  const [form] = useForm()
  const [loading, setLoading] = useState(false)
  const { data: typetantiemes } = useQuery<{ d4_typetantieme: D4_Typetantieme[] }>(TYPE_TANTIEME_QUERY)
  const { data: tantiemelots } = useQuery<{ d4_lot: D4_Lot[] }, { trigramme: string }>(LOTS_QUERY, {
    variables: { trigramme: demande.copropriete?.trigramme ?? '' },
  })
  const filteredTypetantieme = useMemo(() => {
    const typetantiemeLots = uniqBy(
      tantiemelots?.d4_lot.flatMap((lot) => lot.tantiemes),
      'typetantieme_id'
    ).map((t) => t.typetantieme_id)

    const filtered = typetantiemes?.d4_typetantieme.filter((tantieme) => typetantiemeLots.includes(tantieme.id))

    return filtered
  }, [tantiemelots?.d4_lot, typetantiemes?.d4_typetantieme])

  const [createDemandeIntervention] = useMutation(DEMANDE_INTERVENTION_MUTATION)
  const [updateDevisFournisseur] = useMutation<
    { devisfournisseur: D4_Devisfournisseur },
    { id: number; data: D4_Devisfournisseur_Set_Input }
  >(DEVIS_FOURNISSEUR_MUTATION)
  const [updateDemandeDeDevis] = useMutation<
    { devis: D4_Demandedevis },
    { id: number; data: D4_Demandedevis_Set_Input }
  >(CLOTURE_DEVIS_MUTATION)

  const [createEvenementDemande] = useMutation<{}, { data: D4_Evenementdemande_Insert_Input }>(EVENEMENT_ADD_MUTATION)
  // const [fetchDemande] = useLazyQuery<{}, { id: number }>(EVENEMENTS)
  const { data: cs } = useQuery<{ d4_conseilsyndical: D4_Conseilsyndical[] }, { trg: string }>(C_S_QUERY, {
    variables: { trg: demande.copropriete?.trigramme ?? '' },
  })

  const { data: count } = useQuery(DEMANDE_INTERVENTION_COUNT_QUERY, {
    variables: { demandeId: demande.id },
  })
  const [updateDemande] = useDemandeUpdate()

  const { data: fichier } = useQuery<{ fichier: D4_Fichier; id: number }>(FICHIER_QUERY, {
    variables: { id: fichierId },
    skip: !fichierId,
  })

  const [modalSocieteVisible, setModalSocieteVisible] = useState(false)
  const [societeAModifier, setSocieteAModifier] = useState<D4_Societe>()
  const {
    user: {
      personne: { nom, prenom },
    },
  } = useAuth()
  const {
    description,
    batimentlibelle,
    equipementlibelle,
    copropriete: {
      //@ts-expect-error
      trigramme,
      //@ts-expect-error
      personnes,
    },
  } = demande

  // Récupération des éléments checkés

  var checked: any[] = []

  const verifCheck = (e: CheckboxChangeEvent, evenements: D4_Fn_View_Evenements) => {

    if(e.target.checked === true){
      checked[checked.length] = evenements
    }
    else{
      checked.forEach(element => {
        checked = checked.filter(e => e !== evenements)
      })
    }

    return checked
  }

  //

  const [membresCsOptions, membreCsValues] = cs?.d4_conseilsyndical
    ? formatMembreCSOptions(cs?.d4_conseilsyndical ?? [], 'id')
    : []

  const contactsOptions = [
    {
      key: 'concierges',
      label: 'Concierge(s)',
      options: personnes.map((p: any) => ({
        label: `(Concierge) ${p.nom} ${p.prenom}`,
        value: p.id,
      })),
    },
  ]
    //@ts-expect-error
    .concat(membresCsOptions)
    .filter((v) => v)

  const { updateKanban } = useUpdateKanban(demande.id)

  const onFinish = async (values: any) => {
    setLoading(true)

    const societe: D4_Societe = queryCache({
      typename: 'd4_societe',
      id: values.fournisseur_id,
      //@ts-expect-error
      fields: [`mail1`, `raisonsociale`],
    })

    if (!validateEmail(societe?.mail1 ?? '')) {
      message.error("Le mail n'est pas valide")
      form.resetFields(['fournisseur_id'])
      setSocieteAModifier(societe)
      setModalSocieteVisible(true)
      setLoading(false)
      return
    }

    const data: D4_Interventiondemande_Insert_Input = {
      dtype: 'BonDeCommande',
      datecreationdemande: dayjs(),
      daterappel: values.daterappel,
      dateaccordcs: values.dateaccordcs,
      objet: values.objet,
      lieuintervention: values.lieuintervention,
      reference: values.reference,
      demande_id: demande.id,
      fournisseur_id: values.fournisseur_id,
      privatif: values.privatif,
      locatif: values.locatif,
      avecadf: values.avecadf,
      travauxvotes: values.travauxvotes,
      typetantieme_id: values.typetantieme_id,
    }

    const contactsSurPlace = values?.contacts?.map((contact: number) =>
      personnes.concat(membreCsValues).find((p: D4_Personne) => p.id === Number(contact))
    )

    try {
      const res = await createDemandeIntervention({ variables: { data } })
      /**
       * Dans le cas d'une DI depuis un devis
       */
      if (fichierId) {
        if (fichier?.fichier.devisfournisseur_id) {
          const ddevFournisseur = await updateDevisFournisseur({
            variables: {
              id: fichier?.fichier.devisfournisseur_id,
              data: { demandeintervention_id: res.data.insert_d4_interventiondemande_one.id },
            },
          })
          if (ddevFournisseur.data?.devisfournisseur?.demandedevisfournisseur?.demandedevis?.id) {
            await updateDemandeDeDevis({
              variables: {
                id: ddevFournisseur.data?.devisfournisseur.demandedevisfournisseur?.demandedevis?.id,
                data: { datecloture: dayjs() },
              },
            })
          }
        }
      }
      const email = {
        message: {
          subject: `D4 Immobilier / ${demande.copropriete?.nom} / DI (${demande.id})`,
          body: {
            contentType: 'html',
            content: modelMail({
              copro: values.lieuintervention,
              gestionnaire: `${prenom} ${nom}`,
              objet: values.objet,
              mailsBinome: `
                ${demande.copropriete?.gestionnaireSedentaire?.mail1 ?? ''} ou
                ${demande.copropriete?.gestionnaire?.mail1 ?? ''}
              `,
              rappel: values.rappel,
              reference: values.reference,
              contactsSurPlace,
              checked: checked
            }),
          },
          toRecipients: [{ emailAddress: { address: societe.mail1 } }],
          // attachments: [
          //   {
          //     '@odata.type': '#microsoft.graph.fileAttachment',
          //     name: 'bon_de_commande.pdf',
          //     contentType: 'application/pdf',
          //     contentBytes: arrayBufferToBase64(contentBytesDI),
          //   },
          //   ...fichiers,
          // ],
        },
        saveToSentItems: 'true',
      }

      await MSGraphAPI.sendEmail({ data: email })
      await updateKanban(3768204, { label: '', value: 3768204, etatkanbanid: 3768204 })
      await createEvenementDemande({
        variables: {
          data: {
            demande_id: demande.id,
            texte: `Demande d'intervention envoyée à ${societe.raisonsociale} n°${values.reference}`,
          },
        },
        refetchQueries: [
          {
            query: EVENEMENTS,
            variables: { id: demande.id },
          },
        ],
      })

      updateDemande({
        variables: { id: demande.id, data: { datedernierimportmailatraiter: null } },
      })

      // await fetchDemande({ variables: { id: demande.id } })

      form.resetFields()
      setLoading(false)
      onSubmit(res)
    } catch (error) {
      message.error(JSON.stringify(error))
      setLoading(false)
    }
  }

  /**
   * Si il y a un fichierId c'est que la di vient d'un devis donc le message est different
   */
  const messageObjet = fichierId
    ? `Nous vous informons que nous vous donnons notre bon pour accord pour votre devis n°${fichier?.fichier.devis_fournisseur?.reference} d'un montant de ${fichier?.fichier.devis_fournisseur?.montant} €\n\n`
    : `Merci de :
  - nous établir un devis si le montant de l’intervention doit dépasser 300 euros TTC.\n\n`

  useEffect(() => {
    const reference = `${dayjs().format('YYYY-MM-DD')}-${demande.copropriete?.trigramme}-${
      count?.d4_interventiondemande_aggregate?.aggregate?.count + 1
    }${demande.batimentlibelle === 'Général' ? '' : '-' + demande.batimentlibelle}`

    const fournisseur_id =
      fichier?.fichier.devis_fournisseur?.fournisseur?.id ??
      demande.equipement?.fournisseur_favori_equipements?.[0]?.societe_id

    form.setFieldsValue({
      lieuintervention: formatAddress(demande.copropriete as any),
      demande_id: demande.id,
      dateaccordcs: null,
      daterappel: dayjs().add(10, 'day'),
      objet: `${trigramme} > ${batimentlibelle} > ${equipementlibelle} > ${description}\n\n${messageObjet}`,
      reference,
      fournisseur_id,
      privatif: false,
      avecadf: false,
      travauxvotes: false,
    })
  }, [
    batimentlibelle,
    count?.d4_interventiondemande_aggregate?.aggregate?.count,
    demande,
    description,
    equipementlibelle,
    fichier?.fichier.devis_fournisseur?.fournisseur?.id,
    fichier?.fichier.devis_fournisseur?.montant,
    fichier?.fichier.devis_fournisseur?.reference,
    fichierId,
    form,
    messageObjet,
    trigramme,
  ])

  const fournisseursFavoris = demande.equipement?.fournisseur_favori_equipements

  const societeId = fichier?.fichier.devis_fournisseur?.fournisseur?.id
    ? [fichier?.fichier.devis_fournisseur?.fournisseur?.id]
    : fournisseursFavoris?.map((f) => f.societe_id)

  return (
    <>
      <Form
        form={form}
        {...layout}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{ contacts: personnes.map((p: D4_Personne) => p.id), evt: [] }}
      >
        <Row>
          <Col span={8}>
            <Form.Item name="demande_id" noStyle hidden />
            <Form.Item name="rappel" noStyle hidden />
            <Form.Item label="Copropriété">
              <Typography.Text>{trigramme}</Typography.Text>
            </Form.Item>
            <Form.Item name="reference" label="Référence">
              <Input />
            </Form.Item>
            <Form.Item name="coproprietaire" label="Copropriétaire">
              <Coproprietaires
                trigramme={trigramme}
                onChange={(value, option) => {
                  form.setFieldsValue({
                    coproprietaire: value,
                    rappel: `${option.lot_coproprietaire?.coproprietaire.personne?.nom} ${option.lot_coproprietaire?.coproprietaire.personne?.prenom}`,
                  })
                }}
              />
            </Form.Item>
            <Form.Item name="lieuintervention" label="Lieu">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="fournisseur_id" label="Fournisseur">
              <D4SelectSociete societeId={societeId?.filter((s) => s)} />
            </Form.Item>
            <Form.Item noStyle>
              <Typography.Text>Fournisseurs par défaut</Typography.Text>
              <br />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {fournisseursFavoris?.map((f: D4_Fournisseur_Favori_Equipement) => (
                  <span
                    style={{ color: COLOR.green, cursor: 'pointer' }}
                    key={f.id}
                    onClick={() => form.setFieldsValue({ fournisseur_id: f.societe_id })}
                  >
                    {f.societe.raisonsociale}
                  </span>
                ))}
              </div>
            </Form.Item>
            <Form.Item name="objet" label="Objet">
              <Input.TextArea rows={5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="typetantieme_id" label="Type tantieme" required>
              <Select
                showSearch
                listHeight={800}
                optionFilterProp="label"
                options={filteredTypetantieme?.map((t) => ({
                  value: t.id,
                  label: t.type,
                }))}
              />
            </Form.Item>
            <Row>
              <Col span={12}>
                <Form.Item name="privatif" label="Privatif" required>
                  <Radio.Group>
                    <Radio value={true}>Oui</Radio>
                    <Radio value={false}>Non</Radio>
                  </Radio.Group>
                </Form.Item>
                {/* <Form.Item name="locatif" label="Locatif" required>
                <Radio.Group>
                  <Radio value={true}>Oui</Radio>
                  <Radio value={false}>Non</Radio>
                </Radio.Group>
              </Form.Item> */}
              </Col>
              <Col span={12}>
                <Form.Item name="travauxvotes" label="Travaux votés" required>
                  <Radio.Group>
                    <Radio value={true}>Oui</Radio>
                    <Radio value={false}>Non</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name="avecadf" label="Avec appel de fonds" required>
                  <Radio.Group>
                    <Radio value={true}>Oui</Radio>
                    <Radio value={false}>Non</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={8}>
            <Form.Item name="contacts" label="Contacts">
              <Coproprietaires
                mode="tags"
                trigramme={trigramme}
                options={contactsOptions}
                onChange={(value) => {
                  form.setFieldsValue({ contacts: value })
                }}
              />
            </Form.Item>
            <Form.Item name="dateaccordcs" label="Date accord CS">
              <DatePicker format={DATE_FORMAT} />
            </Form.Item>
            <Form.Item name="daterappel" label="Date max avant relance">
              <DatePicker format={DATE_FORMAT} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item {...tailLayout}>
              <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                Envoyer
              </Button>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="evt" label="Ajouter des fichiers">
              <Checkbox.Group>
                {demande.evenements
                  ?.filter((ev) => ev.fichier)
                  ?.map((evt) => (
                    <>
                      <Checkbox onChange={event => verifCheck(event, evt)} value={evt.id}>
                        <>
                          {evt.texte}
                          <Thumbnail texte={evt.texte} />
                        </>
                      </Checkbox>
                      <Divider />
                    </>
                  ))}
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item {...tailLayout}>
          <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
            Envoyer
          </Button>
        </Form.Item>
      </Form>
      <Modal
        visible={modalSocieteVisible}
        onOk={() => setModalSocieteVisible(false)}
        title="Fiche societe"
        width={1200}
      >
        {societeAModifier && (
          <Row>
            <Col span={24}>
              <SocieteDescription societe={societeAModifier} onChange={() => setModalSocieteVisible(false)} />
            </Col>
          </Row>
        )}
      </Modal>
    </>
  )
}

export const DIModalForm: React.FC<ModalProps & { fichierId?: number; demande: D4_Fn_View_Demande }> = ({
  demande,
  fichierId,
  ...rest
}) => {
  return (
    <Modal {...rest} title="Demande d'intervention">
      <DIForm demande={demande} onSubmit={(res: any) => rest?.onCancel?.(res)} fichierId={fichierId} />
    </Modal>
  )
}

export default DIForm
